import React from "react";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { cmsApi } from "../../services/cms";
import { useCMS } from "../../hooks/useCMS";

type VisaDetailItem = {
  title?: string;
  description?: string;
};

type VisaPrice = {
  currency?: string | null;
  amount?: number | null;
};

type VisaDestinationCard = {
  slug: string;
  title: string;
  country?: string | null;
  subtitle?: string;
  subDescription?: string;
  processingTime?: string;
  supportInfo?: string;
  highlights?: string[];
  visaDetails?: VisaDetailItem[];
  price?: VisaPrice | null;
  priceCurrency?: string | null;
  priceAmount?: number | null;
};

const visaHeroContent = {
  title: "Visa Assistance for Popular International Trips,",
  accent: "Built for Indian Travellers",
  description:
    "From Schengen holidays to UK, USA, and worldwide travel, our team helps Indian travellers plan applications with clarity and confidence.",
  highlights: [
    "Destination-specific document guidance",
    "Support for tourist and family visit visas",
    "24/7 customer assistance from inquiry to submission",
  ],
  ctaLabel: "Apply Now",
};

const normalizeCountry = (value?: string | null) =>
  (value || "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();
const canonicalCountry = (value?: string | null) => {
  const normalized = normalizeCountry(value);
  if (!normalized) return "";
  if (normalized === "uae" || normalized === "united arab emirates") {
    return "united arab emirates";
  }
  if (normalized === "in" || normalized === "india") return "india";
  return normalized;
};
const siteRegion = (import.meta.env.VITE_SITE_REGION || "IN").toUpperCase();
const siteCountry = siteRegion === "IN" ? "india" : "united arab emirates";
const isMatchingCountry = (country?: string | null) =>
  !canonicalCountry(country) || canonicalCountry(country) === siteCountry;
const pickDetailByTitle = (visa: VisaDestinationCard, keyword: string) =>
  visa.visaDetails
    ?.find((item) => item?.title?.toLowerCase().includes(keyword))
    ?.description?.trim();

const formatPrice = (visa: VisaDestinationCard) => {
  const amount = visa.price?.amount ?? visa.priceAmount;
  const currency = (visa.price?.currency || visa.priceCurrency || "").toUpperCase();
  const detailPrice = pickDetailByTitle(visa, "price");

  if (typeof amount === "number" && currency) return `From ${currency} ${amount}`;
  if (typeof amount === "number") return `From ${amount}`;
  if (detailPrice) return detailPrice;
  return "-";
};

const VisaServices: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: visaDestinations, loading } =
    useCMS<VisaDestinationCard[]>(cmsApi.getVisaDestinations);

  if (loading) return <div className="py-20 text-center text-white">Loading...</div>;
  const filteredVisaDestinations =
    visaDestinations?.filter((visa) => isMatchingCountry(visa.country)) || [];

  return (
    <section
      id="visa-services"
      className="relative overflow-hidden bg-brand-dark py-20 text-white"
    >
      <div className="absolute right-0 top-0 h-full w-1/2 opacity-10 pointer-events-none">
        <svg viewBox="0 0 200 200" className="h-full w-full">
          <path
            fill="#ef0065"
            d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,81.3,-46.3C90.8,-33.5,96.8,-18,97.1,-2.3C97.4,13.4,92,29.3,82.4,42.4C72.8,55.5,59,65.8,44.2,73.4C29.4,81,14.7,85.9,0.3,85.4C-14.1,84.9,-28.2,79,-41.8,71.5C-55.4,64,-68.5,54.9,-77.4,42.8C-86.3,30.7,-91,15.4,-91.5,0C-92,-15.4,-88.3,-30.8,-80.1,-43.6C-71.9,-56.4,-59.2,-66.6,-45.5,-74.1C-31.8,-81.6,-15.9,-86.4,-0.2,-86C15.5,-85.6,30.6,-83.6,44.7,-76.4Z"
            transform="translate(100 100)"
          />
        </svg>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <span className="text-sm font-semibold uppercase tracking-[0.24em] text-brand-pink">
              {t("Visa Services")}
            </span>

            <h2 className="mt-3 font-heading text-3xl font-bold md:text-4xl lg:text-5xl">
              {t(visaHeroContent.title)}{" "}
              <span className="italic text-brand-pink">
                {t(visaHeroContent.accent)}
              </span>
            </h2>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-300">
              {t(visaHeroContent.description)}
            </p>

            <ul className="mt-8 space-y-4">
              {visaHeroContent.highlights.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-pink/20 text-brand-pink">
                    <FaCheck className="text-xs" />
                  </span>
                  <span className="text-gray-200">{t(item)}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate("/visa-services")}
              className="mt-10 inline-flex items-center gap-2 rounded-xl bg-brand-pink px-8 py-4 font-medium text-white transition-colors hover:bg-brand-pink/90"
            >
              {t(visaHeroContent.ctaLabel)} <FaArrowRight />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {filteredVisaDestinations.slice(0, 3).map((visa, index) => {
              const highlights =
                visa.highlights
                  ?.map((item) => item?.trim())
                  .filter((item): item is string => Boolean(item)) || [];
              const subDescription = visa.subDescription?.trim() || "";

              const processingTime =
                visa.processingTime?.trim() ||
                pickDetailByTitle(visa, "processing") ||
                highlights.find((item) =>
                  /(working\s*day|days?|weeks?|months?|hours?)/i.test(item)
                ) ||
                "-";

              const subtitle =
                visa.subtitle?.trim() ||
                highlights.find(
                  (item) =>
                    item.toLowerCase() !== subDescription.toLowerCase() &&
                    !/(working\s*day|days?|weeks?|months?|hours?)/i.test(item)
                ) ||
                subDescription ||
                "-";

              const priceLabel = formatPrice(visa);

              return (
                <Link
                  key={visa.slug}
                  to={`/visa-services/${visa.slug}`}
                  className={`group rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10 hover:border-brand-pink/30 ${
                    index === 2 ? "sm:col-span-2 sm:justify-self-center" : ""
                  }`}
                >
                  <div className="flex h-full flex-col">
                    <p className="text-lg font-semibold text-white transition-colors group-hover:text-brand-pink">
                      {visa.title}
                    </p>

                    <p className="mt-3 text-sm text-gray-300">
                      <span className="font-medium text-white">{t("Processing Time")}</span> -{" "}
                      {processingTime}
                    </p>

                    <p className="mt-2 text-sm text-gray-300">
                      <span className="font-medium text-white">{t("Price")}</span> -{" "}
                      <span className="text-brand-pink">{priceLabel}</span>
                    </p>

                    <p className="mt-2 text-sm text-gray-300 line-clamp-2">{subtitle}</p>

                    <div className="mt-5 flex items-center justify-between text-sm font-medium text-white/80">
                      <span>{t("View Details")}</span>
                      <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisaServices;
